import { simulationScopeLayers } from "@/lib/strategy/simulation-scope";
import { WORKSPACE_ROUTES } from "@/lib/workspace-routes";
import type { StrategyLayer } from "@/types/strategy";

export type WorkbenchSection = "simulator" | "strategies" | "three-d";

const SECTION_PATHS: Record<WorkbenchSection, string> = {
  simulator: WORKSPACE_ROUTES.workbenchBuild,
  strategies: WORKSPACE_ROUTES.workbenchMonitor,
  "three-d": WORKSPACE_ROUTES.workbenchModel,
};

export function workbenchSectionFromPath(pathname: string): WorkbenchSection {
  if (pathname.endsWith("/monitor") || pathname.endsWith("/strategies")) {
    return "strategies";
  }
  if (pathname.endsWith("/model") || pathname.endsWith("/three-d")) {
    return "three-d";
  }
  return "simulator";
}

export function workbenchHref(
  section: WorkbenchSection,
  search: string,
  extra?: Record<string, string | null>,
) {
  const params = new URLSearchParams(search);
  params.delete("positionRecorded");
  Object.entries(extra ?? {}).forEach(([key, value]) => {
    if (value == null) params.delete(key);
    else params.set(key, value);
  });
  params.delete("positionPanel");
  if (section === "simulator") {
    if (params.get("source") === "workspace") params.delete("template");
    params.delete("direction");
    params.delete("expiryDte");
    params.delete("layer");
    params.delete("leg");
    params.delete("legKey");
    params.delete("layers");
    params.delete("position");
    params.delete("scope");
    params.delete("source");
  }
  if (section === "three-d") {
    const source = params.get("source");
    const explicitPositionLegKey =
      source === "position" &&
      Object.prototype.hasOwnProperty.call(extra ?? {}, "legKey") &&
      extra?.legKey != null;
    if (source !== "workspace") {
      params.delete("expiryDte");
      params.delete("layer");
      params.delete("leg");
      params.delete("layers");
      params.delete("scope");
      if (!explicitPositionLegKey) params.delete("legKey");
    } else if (params.has("layer")) {
      params.delete("expiryDte");
      params.delete("legKey");
      params.delete("layers");
      params.delete("scope");
    } else {
      params.delete("legKey");
    }
  }
  if (section !== "three-d" || params.has("source")) {
    for (const key of [...params.keys()]) {
      if (key === "example" || key.startsWith("example_")) params.delete(key);
    }
  }
  const query = params.toString();
  return query ? `${SECTION_PATHS[section]}?${query}` : SECTION_PATHS[section];
}

// Direct 3D visits use the same selection and date scope as the Build tab.
export function workbenchModelEntryHref(
  search: string,
  workspaceId: string,
  layers: StrategyLayer[],
  state: {
    selectedLayerTab?: unknown;
    selectedExpiryDte?: unknown;
    dte?: unknown;
  },
  holidays: Set<string>,
) {
  const expiryDte =
    typeof state.selectedExpiryDte === "number"
      ? state.selectedExpiryDte
      : null;
  const scopedLayers = simulationScopeLayers(
    layers,
    null,
    expiryDte,
    typeof state.dte === "number" ? state.dte : 30,
    true,
    holidays,
  );
  const selected =
    scopedLayers.find((layer) => layer.id === state.selectedLayerTab) ??
    (scopedLayers.length === 1 ? scopedLayers[0] : null);
  // An expired date filter must not send a direct visit back to itself.
  const availableLayers = scopedLayers.length
    ? scopedLayers
    : layers.filter((layer) => layer.enabled);
  return workbenchNavigationHref(
    "three-d",
    search,
    workspaceId,
    "simulator",
    selected?.id ?? null,
    {
      allLayerIds: availableLayers.map((layer) => layer.id),
      expiryDte: scopedLayers.length ? expiryDte : null,
    },
  );
}

export type WorkbenchSectionSelectHandler = (
  section: WorkbenchSection,
  href: string,
) => void;

export type WorkbenchSectionIntentHandler = (section: WorkbenchSection) => void;

export function workbenchNavigationScrollTop(
  fromSection: WorkbenchSection,
  toSection: WorkbenchSection,
) {
  return fromSection === "three-d" && toSection === "strategies" ? 0 : null;
}

export function workbenchNavigationHref(
  section: WorkbenchSection,
  search: string,
  workspaceId?: string | null,
  fromSection?: WorkbenchSection,
  selectedLayerId?: string | null,
  analysisScope?: {
    allLayerIds?: readonly string[];
    expiryDte?: number | null;
  },
) {
  const opensWorkspaceInThreeD =
    section === "three-d" &&
    fromSection === "simulator" &&
    workspaceId != null &&
    (selectedLayerId != null || (analysisScope?.allLayerIds?.length ?? 0) > 0);
  const allLayerIds = opensWorkspaceInThreeD
    ? [...new Set(analysisScope?.allLayerIds ?? [])].filter(Boolean)
    : [];
  const opensAllScope = selectedLayerId == null && allLayerIds.length > 0;
  return workbenchHref(section, search, {
    direction: null,
    expiryDte:
      opensAllScope && analysisScope?.expiryDte != null
        ? String(Math.round(analysisScope.expiryDte))
        : null,
    leg: null,
    layer: opensWorkspaceInThreeD ? (selectedLayerId ?? null) : null,
    layers: opensAllScope ? allLayerIds.join(",") : null,
    position: null,
    scope: opensAllScope ? "all" : null,
    source: opensWorkspaceInThreeD ? "workspace" : null,
    template: opensWorkspaceInThreeD ? workspaceId : null,
    workspace: workspaceId ?? null,
  });
}

// Compatibility names while non-Workbench callers migrate to the feature API.
export {
  workbenchHref as strategyCenterHref,
  workbenchNavigationHref as strategyCenterNavigationHref,
  workbenchNavigationScrollTop as strategyCenterNavigationScrollTop,
  workbenchSectionFromPath as strategyCenterSectionFromPath,
};
export type {
  WorkbenchSection as StrategyCenterSection,
  WorkbenchSectionIntentHandler as StrategyCenterSectionIntentHandler,
  WorkbenchSectionSelectHandler as StrategyCenterSectionSelectHandler,
};
